import type { CSSProperties, FunctionComponent } from 'react';
import { useEffect, useRef } from 'react';

import { BetLimitModalConnected } from '@pp/core/src/components/betLimits/BetLimitsModalConnected';
import { HelpModalConnected } from '@pp/core/src/components/help/modal/HelpModalConnected';
import { HideDialogsConnected } from '@pp/core/src/components/hideDialogs/HideDialogsConnected';
import { HistoryModalConnected } from '@pp/core/src/components/history/HistoryModalConnected';
import { GameHeader } from '@pp/core/src/components/overlay/gameHeader/GameHeader';
import { QuickGuideModalConnected } from '@pp/core/src/components/quickGuide/QuickGuideModalConnected';
import { SettingsModalConnected } from '@pp/core/src/components/settings/SettingsModalConnected';
import { SubtitlesLazyConnected } from '@pp/core/src/components/subtitles/SubtitlesLazyConnected';
import { useCoreDispatch } from '@pp/core/src/store/CoreStore';
import { useChipSlice } from '@pp/core/src/store/slices/chips/selectors';
import { useConfigSlice } from '@pp/core/src/store/slices/config/selectors';
import { configActions } from '@pp/core/src/store/slices/config/slice';
import { useHeaderSlice } from '@pp/core/src/store/slices/header/selectors';
import { useSubtitlesSlice } from '@pp/core/src/store/slices/subtitles/selectors';
import { useIsLargerThanWide } from '@pp/core/src/styles/hooks/media';
import { useModifiers } from '@pp/core/src/styles/hooks/useModifiers';

import { CrashNavConnected } from '../nav/CrashNavConnected';

import styles from './overlay.module.css';

export const CrashOverlayConnected: FunctionComponent = () => {
  const dispatch = useCoreDispatch();
  const isWide = useIsLargerThanWide();

  const openedModal = useHeaderSlice.getCoreOpenedModal();
  const isClipChipStackOpened = useChipSlice.isClipChipStackOpened();
  const isRevolverChipStackOpened = useChipSlice.isRevolverChipStackOpened();
  const isSubtitlesAvailable = useSubtitlesSlice.isAvailable();
  const playAreaDimensions = useConfigSlice.playAreaDimensions();

  const displayMiniChat = isWide && !openedModal;
  const displaySubtitles = isSubtitlesAvailable && !isRevolverChipStackOpened && !isClipChipStackOpened;

  const gameHeaderOverlayGrid = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!gameHeaderOverlayGrid.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const target = entry.target.children[0] as HTMLDivElement;
        dispatch(configActions.setGameHeaderOverlayHeight(target.clientHeight ?? 0));
      });
    });

    resizeObserver.observe(gameHeaderOverlayGrid.current);

    return () => resizeObserver.disconnect();
  }, [dispatch, gameHeaderOverlayGrid]);

  const chatModalMods = useModifiers(
    styles,
    'overlay__chatModalOrigin',
    { mini: displayMiniChat },
    { withBaseClass: true }
  );

  return (
    <div
      className={styles.overlay}
      style={{ '--playAreaSize': `${playAreaDimensions?.bottomOffset ?? 0}px` } as CSSProperties}
    >
      <div className={styles.overlay__grid} ref={gameHeaderOverlayGrid}>
        <div className={`${styles.overlay__wing} ${styles.overlay__wing_end}`}>
          <CrashNavConnected />
        </div>
        {isWide ? (
          <>
            <div className={`${styles.overlay__wing} ${styles.overlay__wing_start}`}>
              <GameHeader />
            </div>
            <div className={styles.overlay__betLimitModalOrigin}>
              <BetLimitModalConnected />
            </div>
            <div className={chatModalMods}>
              <HideDialogsConnected>
                <QuickGuideModalConnected />
                <HistoryModalConnected />
                <HelpModalConnected />
                <SettingsModalConnected />
                {/* <CrashSettingsModalConnected /> */}
              </HideDialogsConnected>
            </div>
          </>
        ) : null}
        {displaySubtitles ? (
          <div className={styles.overlay__subtitlesOrigin}>
            <SubtitlesLazyConnected />
          </div>
        ) : null}
      </div>
    </div>
  );
};
